import { Col, Row, Form, Select, Radio } from "antd";
import moment from "moment";
import { fetchGenreAudioFeaturesTrend } from "radar/Service/RadarService";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Card, Charts } from "shared/components";
import { filterOptionDiacritics } from "shared/components/single-multiselect/SingleMultiselect";
import {
  RADAR_GENRE_AUDIO_FEATURE_TREND_AUDIO_FEATURE,
  RADAR_GENRE_AUDIO_FEATURE_TREND_AUDIO_FILTER,
  RADAR_GENRE_AUDIO_FEATURE_TREND_AUDIO_GENRES,
} from "shared/constants/LocalStorageConsts";
import { getI18nGenre } from "shared/helpers/i18nGenre";
import useWindowSize from "shared/hooks/useWindowHook";

const defaults = {
  defaultFilter: {},
  defaultAudioFeature: "",
  defaultGenres: [],
};

const colors = [
  "#2E90FA",
  "#32D583",
  "#ADD55D",
  "#B692F6",
  "#75D1D2",
  "#E4A010",
  "#F05576",
  "#FF7514",
  "#ED955E",
  "#7D7F7D",
  "#9762D3",
  "#EB5D53",
];
const initial = () => {
  const savedFilter = localStorage.getItem(
    RADAR_GENRE_AUDIO_FEATURE_TREND_AUDIO_FILTER
  );
  const savedAudioFeature = localStorage.getItem(
    RADAR_GENRE_AUDIO_FEATURE_TREND_AUDIO_FEATURE
  );
  const savedGenres = localStorage.getItem(
    RADAR_GENRE_AUDIO_FEATURE_TREND_AUDIO_GENRES
  );

  const defaultFilter = savedFilter
    ? JSON.parse(savedFilter)
    : defaults.defaultFilter;
  const audioFeature = savedAudioFeature
    ? JSON.parse(savedAudioFeature)
    : defaults.defaultAudioFeature;
  const defaultGenres = savedGenres
    ? JSON.parse(savedGenres)
    : defaults.defaultGenres;

  return {
    defaultFilter,
    audioFeature,
    defaultGenres,
  };
};

const audioFeatureOptions = (t: any) => [
  {
    label: t("pages.AudioFeaturesTrend.danceability"),
    value: "danceability",
  },
  { label: t("pages.AudioFeaturesTrend.energy"), value: "energy" },
  { label: t("pages.AudioFeaturesTrend.speechiness"), value: "speechiness" },
  {
    label: t("pages.AudioFeaturesTrend.acousticness"),
    value: "acousticness",
  },
  {
    label: t("pages.AudioFeaturesTrend.instrumentalness"),
    value: "instrumentalness",
  },
  { label: t("pages.AudioFeaturesTrend.liveness"), value: "liveness" },
  { label: t("pages.AudioFeaturesTrend.valence"), value: "valence" },
  { label: t("pages.AudioFeaturesTrend.tempo"), value: "tempo" },
];

const GenresAudioFeaturesTrend: React.FC<any> = (props) => {
  const { history } = props;
  const { defaultFilter, audioFeature, defaultGenres } = initial();
  const [filter, setFilter] = useState<any>(defaultFilter);
  const [rawTrend, setRawTrend] = useState<any>({});
  const [trend, setTrend] = useState<any>({ series: [], categories: [] });
  const [selectedGenres, setSelectedGenres] = useState<any>(defaultGenres);
  const [selectedDate, setSelectedDate] = useState<any>();
  const [dataIndex, setDataIndex] = useState(-1);
  const [selectedAudioFeature, setSelectedAudioFeature] =
    useState(audioFeature);
  const [currentAudioFeatures, setCurrentAudioFeatures] = useState<any>({
    series: [],
    categories: [],
  });
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation("radar");
  const size: any = useWindowSize();

  const { genres, genreOptions } = useSelector((state: any) => state.genres);

  const wrappGetI18nGenre = useCallback(
    (genre: string) => getI18nGenre(genre, i18n, genres),
    [genres, i18n]
  );

  const onDeleteClick = () => {
    form.setFieldsValue({ monthQuantity: "", size: "" });
    setFilter(defaults.defaultFilter);
    localStorage.setItem(
      RADAR_GENRE_AUDIO_FEATURE_TREND_AUDIO_FILTER,
      JSON.stringify(defaults.defaultFilter)
    );
    localStorage.setItem(
      RADAR_GENRE_AUDIO_FEATURE_TREND_AUDIO_GENRES,
      JSON.stringify(defaults.defaultGenres)
    );
    localStorage.setItem(
      RADAR_GENRE_AUDIO_FEATURE_TREND_AUDIO_FEATURE,
      JSON.stringify(defaults.defaultAudioFeature)
    );

    setSelectedGenres(defaults.defaultGenres);
    setSelectedAudioFeature(defaults.defaultAudioFeature);
    setTrend({
      categories: [],
      series: [],
    });

    setCurrentAudioFeatures({
      categories: [],
      series: [],
    });
  };

  const onCLickChart = (e: any, chart: any, config: any) => {
    setDataIndex(config.dataPointIndex);
    setSelectedDate(rawTrend?.history[config?.dataPointIndex]?.date);
  };

  const onFinishFilter = () => {
    const formFilter = form.getFieldsValue(true);
    localStorage.setItem(
      RADAR_GENRE_AUDIO_FEATURE_TREND_AUDIO_FILTER,
      JSON.stringify(formFilter)
    );
    setFilter({ ...formFilter });
  };

  const onChangeGenre = (value: any) => {
    setSelectedGenres(value);
    localStorage.setItem(
      RADAR_GENRE_AUDIO_FEATURE_TREND_AUDIO_GENRES,
      JSON.stringify(value)
    );
  };

  const onChangeAudioFeature = (value: string) => {
    setSelectedAudioFeature(value);
    localStorage.setItem(
      RADAR_GENRE_AUDIO_FEATURE_TREND_AUDIO_FEATURE,
      value ? JSON.stringify(value) : ""
    );
  };

  const periodOptions = [
    { label: t("pages.TopSpotify.periodMonth", { month: 1 }), value: 1 },
    { label: t("pages.TopSpotify.periodMonths", { months: 3 }), value: 3 },
    { label: t("pages.TopSpotify.periodMonths", { months: 6 }), value: 6 },
    { label: t("pages.TopSpotify.periodYear", { year: 1 }), value: 12 },
    { label: t("pages.TopSpotify.totalPeriod"), value: 0 },
  ];

  useEffect(() => {
    const proccessTrend = () => {
      if (rawTrend?.history && selectedAudioFeature) {
        const genreLocal = Object.keys(rawTrend.latest.genresStrm);
        const series = selectedGenres.map((genre: any) => ({
          name: wrappGetI18nGenre(genre),
          data: [],
        }));
        setTrend(
          rawTrend.history.reduce(
            (acc: any, curr: any) => {
              acc.categories.push(moment(curr.date).format("MMM DD, YYYY"));
              acc.series.forEach((tmp: any, index: number) => {
                tmp.data.push(
                  curr.genresStrm[genreLocal[index]] &&
                    curr.genresStrm[genreLocal[index]][selectedAudioFeature]
                );
              });

              return acc;
            },
            {
              categories: [],
              series,
            }
          )
        );
      }
    };

    proccessTrend();
  }, [rawTrend, selectedAudioFeature, selectedGenres, wrappGetI18nGenre]);

  useEffect(() => {
    const getGenreAudioFeatureTrend = async () => {
      const { monthQuantity } = filter;
      if (monthQuantity || monthQuantity === 0) {
        const response: any = await fetchGenreAudioFeaturesTrend(monthQuantity);
        setRawTrend(response);
      }
    };

    if (filter) getGenreAudioFeatureTrend();
  }, [filter]);

  useEffect(() => {
    const proccessAudioFeatures = () => {
      if (
        (rawTrend?.latest || (rawTrend?.history && dataIndex > -1)) &&
        genres
      ) {
        setCurrentAudioFeatures({
          categories: audioFeatureOptions(t).map(
            (feature: any) => feature.label
          ),
          series: selectedGenres
            .map((genre: any) => ({
              name: wrappGetI18nGenre(genre),
              data: audioFeatureOptions(t).map(
                (feature: any) =>
                  (dataIndex > -1
                    ? rawTrend.history[dataIndex]
                    : rawTrend.latest
                  ).genresStrm[genre] &&
                  (dataIndex > -1
                    ? rawTrend.history[dataIndex]
                    : rawTrend.latest
                  ).genresStrm[genre][feature.value]
              ),
            }))
            .filter((serie: any) =>
              serie.data.every((a: any) => a !== undefined)
            ),
        });
      }
    };
    proccessAudioFeatures();
  }, [rawTrend, genres, dataIndex, selectedGenres, wrappGetI18nGenre, t]);

  return (
    <div className="radar-home">
      <Row gutter={16}>
        <Col xs={24} className="conteudo-home">
          <Form
            form={form}
            initialValues={filter}
            onFinish={onFinishFilter}
            className="filtro"
            layout="vertical"
          >
            <Card style={{ minHeight: "100px" }}>
              <Row gutter={16} justify="center">
                <Col xs={6}>
                  <Form.Item
                    name="monthQuantity"
                    label={t("pages.TopSpotify.period")}
                  >
                    <Select
                      options={periodOptions}
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    />
                  </Form.Item>
                </Col>

                <Col xs={3}>
                  <div className="filter-button ">
                    <span className="delete" onClick={onDeleteClick}>
                      {t("pages.ArtistsRanking.delete")}
                    </span>
                    <button className="apply">
                      {t("pages.ArtistsRanking.filter")}
                    </button>
                  </div>
                </Col>
              </Row>
            </Card>
          </Form>
          <Row style={{ marginTop: "16px" }}>
            <Col style={{ textAlign: "center" }}>
              <Radio.Group value="genresAudiofeaturesTrend">
                <Radio.Button
                  value="trends"
                  onClick={() => history.push("/trends")}
                >
                  {t("pages.AudioFeaturesTrend.trends")}
                </Radio.Button>
                <Radio.Button
                  value="genresAudiofeaturesTrend"
                  onClick={() => history.push("/genres-audio-features-trend")}
                >
                  {t("pages.AudioFeaturesTrend.genreAudioFeatureTrend")}
                </Radio.Button>
              </Radio.Group>
            </Col>
          </Row>
          <Row style={{ marginTop: "16px", marginBottom: "64px" }}>
            <Col xs={24}>
              <Card>
                <Row justify="center" gutter={24} style={{ padding: "16px" }}>
                  <Col xs={12}>
                    <Form.Item
                      label={t("pages.AudioFeaturesTrend.audioFeature")}
                    >
                      <Select
                        options={audioFeatureOptions(t)}
                        value={selectedAudioFeature}
                        onChange={onChangeAudioFeature}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <Form.Item label={t("pages.AudioFeaturesTrend.genres")}>
                      <Select
                        options={genreOptions}
                        value={selectedGenres}
                        mode="multiple"
                        onChange={onChangeGenre}
                        filterOption={filterOptionDiacritics}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xs={16}>
                    <Charts.LineChart
                      {...trend}
                      xAxisFormatter={(val) => (val || 0).toPrecision(3)}
                      xaxisLabelFormat="MM/yy"
                      xaxisType="datetime"
                      onClick={onCLickChart}
                      color={colors}
                    />
                  </Col>
                  <Col
                    xs={8}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      <span
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          fontSize: "18px",
                          color: "white",
                        }}
                      >
                        {selectedDate
                          ? selectedDate
                          : t("pages.AudioFeaturesTrend.latest")}
                      </span>
                      <Charts.RadarChart
                        {...currentAudioFeatures}
                        width={size?.width < 1550 ? 90 : 120}
                        cor={colors}
                        legend={false}
                        showTooltip={false}
                        useI18nLabel={false}
                      />
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default GenresAudioFeaturesTrend;
